import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { AppDialog } from "../../components/AppDialog";
import { useNotify } from "../../components/notifications";
import { useConfirm } from "../../components/confirm";
import { AiCombo } from "../../components/AiCombo";
import { ActionMenu } from "../../components/AppToolbar";
import { COPY_SECTIONS } from "./TestCodeCopyFromPanel";
import { QcFromSampleDialog, QcLimitsBetweenDialog } from "./TestQcCopyDialogs";

/**
 * Test Code → the record-scoped actions on the EDIT detail action bar
 * (prototype v12): **Rename Test · Copy Test · Copy Parameters ▾**.
 *
 * These need a DIALOG rather than a plain POST, which the config-driven
 * `recordActions` cannot express, so they come through the shared
 * `recordActionsSlot`. Audit Trail, Delete and Refresh are already on the
 * shared bar / toolbar and are not duplicated here.
 *
 * "Copy Test" and "Copy Parameters" are the SAME endpoint
 * (`POST {id}/copy`) with a different section set: Copy Test offers all seven,
 * Copy Parameters narrows to one. Both preview first, so the user sees
 * "+N new · M already here" before anything is written — and both are ADDITIVE
 * (owner decision 2026-10-13): nothing already on this test is overwritten and
 * retired rows are never copied.
 */

type TestRow = { oid: string; test: string; matrix: string; method: string };

const SECTION_LABEL = new Map(COPY_SECTIONS.map(([k, l]) => [k, l]));

/** Single-section copies: [dialog title, body heading]. Keyed by section, worded
 *  as the Copy Parameters menu words it (prototype v12). A single-section dialog
 *  shows ONLY Copy From - never the section tick list, which belongs to "Copy
 *  settings from another test" (owner report 2026-10-15). */
const SINGLE_COPY = new Map<string, readonly [string, string, string]>([
  ["SampleParameters", ["Copy Parameters", "Copy sample parameters from", "sample parameters"]],
  ["Surrogates", ["Copy Surrogates", "Copy surrogates from", "surrogates"]],
  ["InternalStandard", ["Copy Internal Standards", "Copy internal standards from", "internal standards"]],
  ["QCLimits", ["Copy QC Parameters", "Copy QC parameters from", "QC parameters"]],
]);

export function TestCodeRecordActions({
  id,
  testName,
  onChanged,
  onCopyTest,
  viewOnly = false,
}: {
  id: string;
  testName?: string;
  onChanged: () => void;
  /** The record is open READ-ONLY (a row click rather than the pencil), so the
   *  three actions here that WRITE are disabled. Rename and Copy Parameters
   *  change this test; Copy Test creates another one. */
  viewOnly?: boolean;
  /** Opens the NEW form pre-filled to copy from this test. Supplied by the host,
   *  which owns the list/detail switch. Without it the button is not rendered —
   *  better than a button that silently does the wrong thing. */
  onCopyTest?: () => void;
}) {
  /* A GROUP test has no Sample Parameters / Surrogates / Internal Standards /
     QC Limits — it carries MEMBERS — so "Copy Parameters" would copy nothing
     into it whichever entry you picked. Legacy keeps the menu off a group test
     for the same reason, and it is hidden rather than disabled: a greyed menu
     invites the user to hunt for what would enable it. */
  const [isGroup, setIsGroup] = useState(false);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get(`/api/v1/test-methods/${id}`);
        if (alive) setIsGroup(!!data?.data?.isGroup);
      } catch {
        // Unknown reads as "not a group": the ordinary menu is the safe default,
        // and the shared axios handling already surfaced the failure.
        if (alive) setIsGroup(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const [renameOpen, setRenameOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySections, setCopySections] = useState<string[]>([]);
  const [qcFromSample, setQcFromSample] = useState(false);
  const [qcBetween, setQcBetween] = useState(false);
  const notify = useNotify();
  const { confirm } = useConfirm();

  const openCopy = (sections: string[]) => {
    setCopySections(sections);
    setCopyOpen(true);
  };

  return (
    <>
      <button className="sd-abtn" onClick={() => setRenameOpen(true)} disabled={viewOnly}>
        <span className="tb-i tb-i-blue">✎</span> Rename Test
      </button>
      {/* ⚠️ Copy Test creates a NEW TEST that is a copy of this one — it does NOT
          copy into this one. That is "Copy Parameters", below. The two were the
          same action until 2026-10-14, which made Copy Test a slower duplicate of
          Copy Parameters and gave the lab no way to clone a test at all
          (prototype v12: openNew(src) → "New test pre-filled to copy from X"). */}
      {onCopyTest && (
        <button className="sd-abtn" onClick={onCopyTest} disabled={viewOnly}>
          <span className="tb-i">⧉</span> Copy Test
        </button>
      )}
      {/* The SHARED popup menu (standing rule 8), so this reads and behaves
          exactly like every "Export to ▾" on the app — same card, rows, hover
          and cursor — and inherits its keyboard contract (↑/↓ · Home/End ·
          Enter · Esc) and viewport clamping. It was a hand-rolled .tc-menu
          with its own flat buttons until 2026-10-30 (owner: "set same design
          and layout for the copy parameters").
          The ROWS are the prototype's: a group header, the all-sections entry
          first, then the four single-section copies, then the operations that
          stay INSIDE this test (prototype v12 lines 678-681). */}
      {!isGroup && (
        <ActionMenu
          label="Copy Parameters"
          icon="ti-copy"
          title="Copy Parameters"
          triggerClassName="sd-abtn"
          disabled={viewOnly}
          items={[
            { id: "h1", kind: "header", label: "From another test", onSelect: () => {} },
            {
              id: "all",
              label: "Copy settings from another test…",
              onSelect: () => openCopy(COPY_SECTIONS.map(([k]) => k)),
            },
            { id: "sp", label: "Copy parameters", onSelect: () => openCopy(["SampleParameters"]) },
            { id: "surr", label: "Copy surrogates", onSelect: () => openCopy(["Surrogates"]) },
            { id: "istd", label: "Copy internal standards", onSelect: () => openCopy(["InternalStandard"]) },
            { id: "qc", label: "Copy QC parameters", onSelect: () => openCopy(["QCLimits"]) },
            { id: "sep", kind: "separator", label: "", onSelect: () => {} },
            { id: "h2", kind: "header", label: "Within this test", onSelect: () => {} },
            {
              id: "qcsame",
              label: "Copy QC parameters from same test…",
              onSelect: () => setQcFromSample(true),
            },
            {
              id: "qcbetween",
              label: "Copy QC limits between QC types…",
              onSelect: () => setQcBetween(true),
            },
          ]}
        />
      )}

      <QcFromSampleDialog
        open={qcFromSample}
        base={`/api/v1/test-methods/${id}`}
        testName={testName}
        onClose={() => setQcFromSample(false)}
        onDone={onChanged}
      />
      <QcLimitsBetweenDialog
        open={qcBetween}
        base={`/api/v1/test-methods/${id}`}
        onClose={() => setQcBetween(false)}
        onDone={onChanged}
      />
      <RenameDialog
        open={renameOpen}
        id={id}
        current={testName ?? ""}
        onClose={() => setRenameOpen(false)}
        onDone={onChanged}
        notify={notify}
        confirm={confirm}
      />
      <CopyDialog
        open={copyOpen}
        id={id}
        targetName={testName}
        sections={copySections}
        onClose={() => setCopyOpen(false)}
        onDone={onChanged}
        notify={notify}
        confirm={confirm}
      />
    </>
  );
}

/* ── Rename Test ───────────────────────────────────────────────────────────
   Legacy renames IN PLACE: it edits TestName and nothing else — no cascade, no
   TestCode regeneration (RenameTest_Execute → dc_Accepting, :5832). The only
   check is the class-level Matrix+Test+Method uniqueness, which the existing PUT
   already enforces, so this reuses that endpoint rather than adding one.        */
function RenameDialog({
  open, id, current, onClose, onDone, notify, confirm,
}: {
  open: boolean; id: string;
  /** The caller's name, when it has one. The only call site does not pass it, so
   *  the dialog reads the record itself — see below. */
  current?: string;
  onClose: () => void; onDone: () => void;
  notify: ReturnType<typeof useNotify>;
  confirm: ReturnType<typeof useConfirm>["confirm"];
}) {
  const [name, setName] = useState("");
  const [loaded, setLoaded] = useState("");
  const [busy, setBusy] = useState(false);

  // Read the name from the RECORD. The `current` prop was never passed by the
  // one call site (recordActionsSlot hands over the id alone), so "Current name"
  // rendered empty on every test — the same slip as the setup strip's General
  // chip. Reading it here means the field cannot be blank, whoever mounts this.
  useEffect(() => {
    if (!open) return;
    setName("");
    setLoaded(current ?? "");
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get(`/api/v1/test-methods/${id}`);
        const n = String(data?.data?.testName ?? "");
        // Only the READ-ONLY "Current name" is filled. "New name" stays EMPTY:
        // prefilling it with the current name makes an unchanged value look like
        // a rename and leaves the user deleting text before they can type
        // (owner report 2026-10-15).
        if (alive && n) setLoaded(n);
      } catch {
        /* The field falls back to whatever the caller passed; Save still works. */
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, id, current]);

  if (!open) return null;

  const save = async () => {
    const next = name.trim();
    // Legacy's verbatim refusal (dc_Accepting :5836).
    if (!next) {
      notify.warning("Test name cannot be empty.");
      return;
    }
    if (next === loaded.trim()) {
      onClose();
      return;
    }
    if (!(await confirm({ title: "Rename Test", message: `Rename to "${next}"?` }))) return;
    setBusy(true);
    try {
      // Read-modify-write: the PUT takes the whole record, so send the loaded
      // values with only TestName changed rather than inventing a rename route.
      const { data } = await api.get(`/api/v1/test-methods/${id}`);
      const cur = data?.data ?? {};
      await api.put(`/api/v1/test-methods/${id}`, {
        testName: next,
        matrixId: cur.matrixId,
        methodId: cur.methodId,
        category: cur.category,
        isGroup: !!cur.isGroup,
        isFieldTest: !!cur.isFieldTest,
        retireDate: cur.retireDate ? String(cur.retireDate).slice(0, 10) : null,
        comment: cur.comment,
        departmentId: cur.departmentId,
        isPlmTest: !!cur.isPlmTest,
      });
      notify.success("Test renamed successfully.");
      onDone();
      onClose();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "Could not rename the test.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppDialog
      open={open}
      title="Rename Test"
      width={460}
      onClose={onClose}
      className="dlg-center"
      footer={
        <>
          {/* The shared BO-standard solid orange primary (theme/boStandard.css). */}
          {/* Dead until a new name is typed that actually differs - renaming a
              test to what it is already called is a no-op the audit trail would
              still record. */}
          <button
            className="ca-btn ca-btn-solid"
            onClick={() => void save()}
            disabled={busy || !name.trim() || name.trim() === loaded.trim()}
          >
            OK
          </button>
          <button className="ca-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        </>
      }
    >
      {/* Department-form metrics (the AI standard reference form): left-aligned
          side labels in normal weight, 24px square controls, 6px row gap. */}
      <div className="tc-copy-grid tc-rename-grid sd-grid--side">
        {/* Read-only - the point is to see what you are renaming FROM while you
            type the new name. */}
        <label className="tc-copy-lbl">Current name<span className="req">*</span></label>
        <input className="sd-input readonly" value={loaded} readOnly />

        <label className="tc-copy-lbl">New name<span className="req">*</span></label>
        <input className="sd-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </div>
    </AppDialog>
  );
}

/* ── Copy Test / Copy Parameters ───────────────────────────────────────────── */
function CopyDialog({
  open, id, targetName, sections, onClose, onDone, notify, confirm,
}: {
  open: boolean; id: string; targetName?: string; sections: string[]; onClose: () => void; onDone: () => void;
  notify: ReturnType<typeof useNotify>;
  confirm: ReturnType<typeof useConfirm>["confirm"];
}) {
  const [rows, setRows] = useState<TestRow[]>([]);
  const [matrix, setMatrix] = useState("");
  const [testName, setTestName] = useState("");
  const [sourceOid, setSourceOid] = useState("");
  const [preview, setPreview] = useState<{ section: string; added: number; existing: number; retiredSkipped: number }[] | null>(null);
  const [busy, setBusy] = useState(false);
  // The prototype lets you TICK which settings travel; the menu item only SEEDS
  // this (owner request 2026-10-15). Re-seeded on every open, so choosing
  // "Copy surrogates" never inherits a wider selection from last time.
  const [picked, setPicked] = useState<Set<string>>(() => new Set(sections));
  useEffect(() => { if (open) setPicked(new Set(sections)); }, [open, sections]);

  // Per-section preview, keyed for the inline row status. The prototype shows the
  // result on the row itself rather than in a separate panel (v12 line 846).
  const planBy = useMemo(
    () => new Map((preview ?? []).map((r) => [r.section, r])),
    [preview],
  );
  // Only TICKED sections count toward the total, and Copy stays disabled while it
  // is zero - same gate the prototype applies (v12 line 848).
  const totalAdd = useMemo(
    () => [...picked].reduce((a, k) => a + (planBy.get(k)?.added ?? 0), 0),
    [picked, planBy],
  );

  // A SINGLE-section copy states the result in words rather than as a row chip:
  // "VOC has 10 sample parameters: 9 will be added, 0 already on this test will
  // be skipped. 1 retired on the source are not copied." (prototype copyPreview,
  // v12 line 834; owner report 2026-10-15). The source's total is Added +
  // Existing + RetiredSkipped - the SP splits the source's live rows into the
  // first two and counts its retired ones separately - so no extra round trip.
  const single = sections.length === 1 ? sections[0]! : null;
  const singleNote = useMemo(() => {
    if (!single || !sourceOid || !preview) return null;
    // No row for the section means the source carries none of them - a real
    // answer, not a missing one, so it reports zero rather than disappearing.
    const plan = planBy.get(single) ?? { added: 0, existing: 0, retiredSkipped: 0 };
    const noun = SINGLE_COPY.get(single)?.[2] ?? "items";
    const total = plan.added + plan.existing + plan.retiredSkipped;
    return { ...plan, noun, total, name: testName || "That test" };
  }, [single, sourceOid, preview, planBy, testName]);

  // Untick every section with nothing new the moment a preview lands.
  useEffect(() => {
    if (!preview) return;
    setPicked(new Set(
      COPY_SECTIONS.filter(([k]) => (planBy.get(k)?.added ?? 0) > 0).map(([k]) => k),
    ));
    // planBy is derived from preview; depending on it too would re-run this on
    // every render and overwrite the user's own ticks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  useEffect(() => {
    if (!open) return;
    setMatrix(""); setTestName(""); setSourceOid(""); setPreview(null);
    void (async () => {
      try {
        const { data } = await api.get("/api/v1/modules/set-testcode", { params: { pageSize: 1000 } });
        setRows((data?.data ?? data?.rows ?? []).filter((r: TestRow) => r.oid !== id));
      } catch {
        setRows([]);
      }
    })();
  }, [open, id]);

  const matrices = useMemo(() => [...new Set(rows.map((r) => r.matrix))].sort(), [rows]);
  const tests = useMemo(() => {
    const seen = new Set<string>();
    return rows
      .filter((r) => !matrix || r.matrix === matrix)
      .filter((r) => (seen.has(r.test) ? false : (seen.add(r.test), true)))
      .sort((a, b) => a.test.localeCompare(b.test));
  }, [rows, matrix]);
  const methods = useMemo(
    () => rows.filter((r) => (!matrix || r.matrix === matrix) && r.test === testName),
    [rows, matrix, testName],
  );

  // Preview as soon as a source is chosen: the prototype shows "+N new ·
  // M already here" BEFORE the copy, and it costs nothing (the SP writes
  // nothing in preview mode).
  useEffect(() => {
    if (!sourceOid) { setPreview(null); return; }
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.post(`/api/v1/test-methods/${id}/copy/preview`, { sourceOid, sections: [...picked] });
        if (alive) setPreview(data?.data ?? []);
      } catch {
        if (alive) setPreview(null);
      }
    })();
    return () => { alive = false; };
  }, [sourceOid, id, sections]);

  // Sections the preview says would actually add rows. With no preview yet (no
  // source chosen, or the call failed) every section counts, so the dialog
  // behaves exactly as it did before.
  const addsRows = ([k]: readonly [string, string]) => !preview || (planBy.get(k)?.added ?? 0) > 0;

  if (!open) return null;

  const run = async () => {
    if (!sourceOid) {
      notify.warning("Choose the Matrix, Test and Method to copy from.");
      return;
    }
    const total = (preview ?? []).reduce((a, p) => a + p.added, 0);
    const ok = await confirm({
      title: sections.length > 1 ? "Copy Test" : "Copy Parameters",
      message: total === 0
        ? "Nothing new to copy — this test already has everything from that source. Continue anyway?"
        : `Copy ${total} row${total === 1 ? "" : "s"} into this test? Anything already here is left as it is.`,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/v1/test-methods/${id}/copy`, { sourceOid, sections: [...picked] });
      const added = (data?.data ?? []).reduce((a: number, p: { added: number }) => a + p.added, 0);
      notify.success(added === 0 ? "Nothing to copy — no change." : `Copied ${added} row${added === 1 ? "" : "s"}.`);
      onDone();
      onClose();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Copy failed.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppDialog
      open={open}
      // The prototype's own caption. "Copy Test" was wrong here: that is the
      // OTHER action, which creates a NEW test (owner report 2026-10-15).
      title={
        sections.length > 1
          ? "Copy settings from another test"
          : SINGLE_COPY.get(sections[0]!)?.[0] ?? `Copy ${SECTION_LABEL.get(sections[0]!) ?? "Parameters"}`
      }
      width={560}
      onClose={onClose}
      className="dlg-center"
      footer={
        <>
          {/* "N item(s) will be added", as the prototype puts it (v12 line 848). */}
          <span className="tc-copy-count">{sections.length > 1 && sourceOid ? `${totalAdd} item(s) will be added` : ""}</span>
          <button className="ca-btn ca-btn-solid" onClick={() => void run()} disabled={busy || !sourceOid || totalAdd === 0}>Copy</button>
          <button className="ca-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        </>
      }
    >
      <div className="tc-copy-h">
        {sections.length > 1 ? "Copy From" : SINGLE_COPY.get(sections[0]!)?.[1] ?? "Copy From"}
      </div>
      {/* The shared AiCombo, as everywhere else on this form — not a native
          <select>, whose browser chrome and yellow default background look
          nothing like the form around it. */}
      <div className="tc-copy-grid">
        <label className="tc-copy-lbl">Matrix<span className="req">*</span></label>
        <AiCombo
          value={matrix || null}
          options={matrices.map((m) => ({ value: m, label: m }))}
          placeholder="— Select —"
          onChange={(v) => { setMatrix(v ?? ""); setTestName(""); setSourceOid(""); }}
        />
        <label className="tc-copy-lbl">Test<span className="req">*</span></label>
        <AiCombo
          value={testName || null}
          options={tests.map((r) => ({ value: r.test, label: r.test }))}
          placeholder="— Select —"
          onChange={(v) => { setTestName(v ?? ""); setSourceOid(""); }}
        />
        <label className="tc-copy-lbl">Method<span className="req">*</span></label>
        <AiCombo
          value={sourceOid || null}
          options={methods.map((r) => ({ value: r.oid, label: r.method || "(no method)" }))}
          placeholder="— Select —"
          disabled={!testName}
          onChange={(v) => setSourceOid(v ?? "")}
        />
      </div>

      {/* Only the multi-section dialog ("Copy settings from another test") shows
          the tick list. A single-section copy is just Copy From - the section is
          already decided by the menu item (owner report 2026-10-15). */}
      {sections.length > 1 && (
        <>
        {/* The prototype's second section: tick which settings travel. The menu
            item only SEEDS this - the user can change it here before copying
            (owner request 2026-10-15). Only the seven keys the SP accepts are
            offered; an unknown key is refused server-side (THROW 50444). */}
        <div className="tc-copy-h" style={{ marginTop: 12 }}>
          Copy Settings{targetName ? ` \u2192 into ${targetName}` : ""}
        </div>
        <div className="tc-copy-list">
          {/* Header row, as the prototype draws it: the column of section names is
              captioned "Title" and carries the select-all tick. It is NOT an eighth
              copyable section - dbo.TestMethod has no Title column and the
              prototype's own COPY_SECTIONS holds exactly the seven below (owner
              confirmed 2026-10-15). Three-state like the shared AppDataGrid:
              checked when all are on, indeterminate on a subset. */}
          <label className="tc-copy-item tc-copy-head">
            <input
              type="checkbox"
              checked={picked.size > 0 && picked.size === COPY_SECTIONS.filter(addsRows).length}
              ref={(el) => {
                if (el) {
                  el.indeterminate =
                    picked.size > 0 && picked.size < COPY_SECTIONS.filter(addsRows).length;
                }
              }}
              onChange={(e) =>
                // Select-all ticks only what would add something - re-ticking a
                // section with nothing new would undo the rule above.
                setPicked(e.target.checked
                  ? new Set(COPY_SECTIONS.filter(addsRows).map(([k]) => k))
                  : new Set())
              }
              aria-label="Select all settings"
            />
            Title
          </label>
          {COPY_SECTIONS.map(([k, label]) => (
            <label key={k} className="tc-copy-item">
              <input
                type="checkbox"
                checked={picked.has(k)}
                onChange={(e) => {
                  const next = new Set(picked);
                  if (e.target.checked) next.add(k); else next.delete(k);
                  setPicked(next);
                }}
              />
              {label}
              {/* Row status, as the prototype draws it: "+N new" in bold, else
                  "nothing new", with " · N already here" when the target already
                  holds some. Blank until a source is chosen. */}
              <span className="tc-copy-r">
                {planBy.has(k) ? (
                  <>
                    {planBy.get(k)!.added ? <b>+{planBy.get(k)!.added} new</b> : "nothing new"}
                    {planBy.get(k)!.existing ? ` · ${planBy.get(k)!.existing} already here` : ""}
                  </>
                ) : null}
              </span>
            </label>
          ))}
        </div>
        </>
      )}

      {/* The ONLY note left is the counted, source-specific one a single-section
          copy shows. The owner had both the no-source prompt and the generic
          "adds only what this test doesn't have yet" sentence removed
          (2026-10-15): the required-field markers, the per-row chips and the
          footer count already say all of it, and Copy is disabled until a
          source is picked. The run() guard keeps the prompt wording. */}
      {singleNote ? (
        /* The counted, source-specific message the prototype shows for a
           single-section copy. It warns (amber) when nothing would be added,
           exactly as .preview.warn does there. */
        <div className={`tc-copy-note${singleNote.added ? "" : " tc-copy-note--warn"}`}>
          {singleNote.total === 0 ? (
            /* Nothing to copy at all - the sentence stops there, since every
               clause after it would read "0". */
            `${singleNote.name} has 0 ${singleNote.noun}.`
          ) : (
            <>
              {singleNote.name} has {singleNote.total} {singleNote.noun}:{" "}
              <b>{singleNote.added} will be added</b>
              {singleNote.existing
                ? `, ${singleNote.existing} already on this test will be skipped.`
                : "."}
              {singleNote.retiredSkipped
                ? ` ${singleNote.retiredSkipped} retired on the source are not copied.`
                : ""}
            </>
          )}
        </div>
      ) : null}

    </AppDialog>
  );
}
